import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { conflictResources } from '../data/conflictResources';

export const ConflictAnalysis = () => {
  const [activeTab, setActiveTab] = useState('analyse'); // 'analyse' or 'ressurser'
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [report, setReport] = useState(null);
  const [formData, setFormData] = useState({
    partyA: '',
    partyB: '',
    description: '',
    duration: '1-3 måneder',
    intensity: 5
  });

  const handleAnalyze = () => {
    setIsAnalyzing(true);
    setTimeout(() => {
      const intensity = parseInt(formData.intensity);
      const desc = formData.description.toLowerCase();
      let type = "Oppgavekonflikt";
      
      if (desc.includes("person") || desc.includes("følelser") || desc.includes("behandler")) type = "Relasjonskonflikt";
      if (desc.includes("verdi") || desc.includes("prinsipp") || desc.includes("uærlig")) type = "Verdikonflikt";
      if (desc.includes("info") || desc.includes("beskjed") || desc.includes("forstått")) type = "Kommunikasjonskonflikt";

      let risk = "Lav";
      if (intensity > 4) risk = "Medium";
      if (intensity > 7) risk = "Høy";
      if (intensity > 9 || formData.duration === "Over 1 år") risk = "Kritisk";

      const suggestedActions = [];
      if (risk === "Lav") suggestedActions.push("Fasilitert dialog", "Lederstøtte");
      if (risk === "Medium") suggestedActions.push("Mekling", "Team-workshop");
      if (risk === "Høy") suggestedActions.push("Formell mekling", "Lederintervensjon");
      if (risk === "Kritisk") suggestedActions.push("Formell granskning", "Ekstern bistand");

      setReport({ type, risk, suggestedActions });
      setIsAnalyzing(false);
    }, 1500);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Konflikthåndtering</h1>
          <p className="text-slate-500 mt-1">Verktøy for analyse og praktisk løsning av mellommenneskelige gnisninger.</p>
        </div>
        <div className="flex bg-slate-100 p-1 rounded-xl">
          <button 
            onClick={() => setActiveTab('analyse')}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === 'analyse' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Analyse-motor
          </button>
          <button 
            onClick={() => setActiveTab('ressurser')}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === 'ressurser' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Ressursbank
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'analyse' ? (
          <motion.div 
            key="analyse"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            {!report ? (
              <Card>
                <CardHeader title="Diagnostisk Kartlegging" subtitle="AI-støttet analyse av konfliktens natur og risikoprofil." />
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700">Part A</label>
                      <input 
                        type="text" 
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none" 
                        placeholder="Navn eller team..."
                        value={formData.partyA}
                        onChange={(e) => setFormData({...formData, partyA: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700">Part B</label>
                      <input 
                        type="text" 
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none" 
                        placeholder="Navn eller team..."
                        value={formData.partyB}
                        onChange={(e) => setFormData({...formData, partyB: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700">Situasjonsbeskrivelse</label>
                    <textarea 
                      rows="4" 
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none" 
                      placeholder="Hva har skjedd? Hvordan påvirker det arbeidet?"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700">Intensitet (1-10)</label>
                      <input 
                        type="range" min="1" max="10" 
                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                        value={formData.intensity}
                        onChange={(e) => setFormData({...formData, intensity: e.target.value})}
                      />
                      <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase">
                        <span>Uenighet</span>
                        <span>Full eskalering</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700">Varighet</label>
                      <select 
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white"
                        value={formData.duration}
                        onChange={(e) => setFormData({...formData, duration: e.target.value})}
                      >
                        <option>Under 1 måned</option>
                        <option>1-3 måneder</option>
                        <option>3-6 måneder</option>
                        <option>Over 6 måneder</option>
                      </select>
                    </div>
                  </div>
                  <Button 
                    className="w-full py-4 text-lg" 
                    onClick={handleAnalyze} 
                    disabled={isAnalyzing || !formData.description}
                  >
                    {isAnalyzing ? "Analyserer..." : "Generer Risikorapport"}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <Card className="border-l-4 border-l-indigo-600">
                    <CardHeader title="Analyseresultat" subtitle={`Relasjon: ${formData.partyA} vs ${formData.partyB}`} />
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="p-5 bg-slate-50 rounded-2xl">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Type</p>
                        <h4 className="text-xl font-bold text-slate-900">{report.type}</h4>
                      </div>
                      <div className="p-5 bg-slate-50 rounded-2xl">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Risikonivå</p>
                        <h4 className={`text-xl font-bold ${report.risk === 'Kritisk' || report.risk === 'Høy' ? 'text-rose-600' : 'text-emerald-600'}`}>{report.risk}</h4>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader title="Anbefalte Tiltak" />
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {report.suggestedActions.map((action, i) => (
                        <div key={i} className="flex items-center gap-3 p-4 bg-indigo-50 rounded-xl text-indigo-700 font-bold text-sm">
                          <SafeIcon name="CheckCircle" />
                          {action}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>
                <Card className="bg-slate-900 border-none text-white">
                  <CardContent className="p-8 space-y-6">
                    <SafeIcon name="FileText" className="text-4xl text-indigo-400" />
                    <h3 className="text-xl font-bold">Lederveiledning</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">
                      Vi har generert en skreddersydd veileder for hvordan du bør håndtere denne spesifikke situasjonen de neste 14 dagene.
                    </p>
                    <Button variant="secondary" className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20">
                      Last ned PDF
                    </Button>
                    <button 
                      onClick={() => setReport(null)}
                      className="w-full text-xs text-slate-500 hover:text-white transition-colors"
                    >
                      Start ny analyse
                    </button>
                  </CardContent>
                </Card>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div 
            key="ressurser"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            {/* Interaktive manus */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {conflictResources.scripts.map((script) => (
                <Card key={script.id} hover={true}>
                  <CardHeader title={script.title} subtitle={script.description} icon={<SafeIcon name="MessageSquare" />} />
                  <CardContent className="space-y-4">
                    {script.steps.map((step, i) => (
                      <div key={i} className="space-y-1">
                        <span className="text-[10px] font-black text-indigo-500 uppercase">{step.label}</span>
                        <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-700 italic border-l-2 border-indigo-200">
                          "{step.text}"
                        </div>
                      </div>
                    ))}
                    <Button variant="ghost" size="sm" className="w-full mt-2" icon={<SafeIcon name="Copy" />}>Kopier manus</Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* De-eskalerings-teknikker */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {conflictResources.techniques.map((tech, i) => (
                <Card key={i} className="border-none shadow-sm bg-white">
                  <CardContent className="p-6 text-center space-y-3">
                    <div className={`w-12 h-12 rounded-2xl bg-${tech.color}-50 text-${tech.color}-600 flex items-center justify-center mx-auto`}>
                      <SafeIcon name={tech.icon} className="text-xl" />
                    </div>
                    <h4 className="font-bold text-slate-900">{tech.title}</h4>
                    <p className="text-xs text-slate-500 leading-relaxed">{tech.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Meklings-prosessen */}
            <Card>
              <CardHeader 
                title="Meklingsveileder" 
                subtitle="En strukturert 6-stegs prosess for formelle møter." 
                icon={<SafeIcon name="Users" />}
              />
              <CardContent>
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-100 -z-10"></div>
                  <div className="space-y-6">
                    {conflictResources.mediationSteps.map((step, i) => (
                      <div key={i} className="flex gap-6 items-start">
                        <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-lg shadow-indigo-100">
                          {i + 1}
                        </div>
                        <div>
                          <h5 className="font-bold text-slate-900 text-sm">{step.title}</h5>
                          <p className="text-xs text-slate-500 mt-1 leading-relaxed">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};